'use client';

import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Header } from "@/components/header";
import { Footer } from "@/components/footer";
import { Partners } from '@/components/partners';
import axios from 'axios';
import { useToast } from '@/hooks/use-toast';

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  company: string;
  phone: string;
  country: string;
  cameraCount: number;
  channelSegment: string;
  agreeTerms: boolean;
  subscribe: boolean;
}

export default function FreeTrialPage() {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();

  const [formData, setFormData] = React.useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    phone: '',
    country: '',
    cameraCount: 1,
    channelSegment: '',
    agreeTerms: false,
    subscribe: true
  });

  const [submitting, setSubmitting] = React.useState(false);
  const [submitted, setSubmitted] = React.useState(false);
  const [duplicate, setDuplicate] = React.useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (submitted || duplicate) {
      panelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [submitted, duplicate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;

    setSubmitting(true);
    setSubmitted(false);
    setDuplicate(false);

    try {
      const payload = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim(),
        company: formData.company.trim(),
        phone: formData.phone.trim(),
        country: formData.country,
        cameraCount: formData.cameraCount,
        channelSegment: formData.channelSegment,
        agreeTerms: formData.agreeTerms,
        subscribe: formData.subscribe,
      };

      console.log('Submitting free trial:', payload);

      const response = await axios.post(
        `${API_BASE_URL}/free-trial`,
        payload,
        {
          headers: { 'Content-Type': 'application/json' },
          validateStatus: (status) => status < 500 // Allow 4xx
        }
      );

      console.log('Free trial response:', response.status, response.data);

      // Handle duplicate
      if (response.status === 409 || response.data?.error === 'email_exists') {
        setDuplicate(true);
        toast({
          title: t('contactForm.toast.submissionError', 'Already Submitted'),
          description: response.data?.message || t('contactForm.toast.emailExists', 'You have already requested a free trial.'),
          variant: 'destructive',
        });
        return;
      }

      // Handle validation errors
      if (response.status >= 400 && response.status < 500) {
        const field = response.data?.field;
        const fieldLabel = field ? t(`freeTrial.form.${field}`) : '';
        const msg = field 
          ? `${fieldLabel} is required`
          : response.data?.message || 'Please check all required fields.';
        
        toast({
          title: 'Validation Error',
          description: msg,
          variant: 'destructive',
        });
        return;
      }

      // Success
      if (!response.data?.ok) {
        throw new Error('Invalid response');
      }

      toast({
        title: t('contactForm.toast.requestSubmitted', 'Request Submitted'),
        description: t('contactForm.toast.requestSubmittedDesc', 'Our team will get back to you shortly.'),
      });

      // Optional: Subscribe to newsletter
      if (formData.subscribe) {
        try {
          await axios.post(`${API_BASE_URL}/newsletter`, {
            email: formData.email.trim(),
            firstName: formData.firstName.trim(),
            lastName: formData.lastName.trim(),
            company: formData.company.trim(),
            phone: formData.phone.trim(),
            language: i18n.language,
            source: 'free-trial',
            tags: ['website', 'free-trial'],
          });
        } catch (err) {
          console.warn('Newsletter subscription failed (non-critical):', err);
        }
      }

      setSubmitted(true);

    } catch (error: any) {
      console.error('Free trial submission error:', error);
      toast({
        title: 'Error',
        description: 'Something went wrong. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    if (name === 'cameraCount') {
      const num = parseInt(value) || 1;
      setFormData(prev => ({ ...prev, cameraCount: Math.min(100, Math.max(1, num)) }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const countries = [
    'United States', 'Canada', 'United Kingdom', 'Australia', 'France', 'Germany', 'Spain', 'Other'
  ];

  const channelSegments = [
    { value: 'integrator', label: t('freeTrial.businessSegments.integrator') },
    { value: 'reseller', label: t('freeTrial.businessSegments.reseller') },
    { value: 'surveillance_station', label: t('freeTrial.businessSegments.surveillance_station') },
    { value: 'promoter', label: t('freeTrial.businessSegments.promoter') },
    { value: 'enterprise', label: t('freeTrial.businessSegments.enterprise') },
    { value: 'public_sector', label: t('freeTrial.businessSegments.public_sector') },
    { value: 'SMB', label: t('freeTrial.businessSegments.SMB') },
    { value: 'other', label: t('freeTrial.businessSegments.other') }
  ];

  return (
    <div className="flex flex-col min-h-screen">
      <Header />

      <main className="flex-grow py-12 md:py-16 lg:py-20 flex items-center justify-center">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-3xl mx-auto w-full">
            <div className="text-center mb-12">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-4">
                {t('freeTrial.title')}
              </h1>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                {t('freeTrial.subtitle')}
              </p>
            </div>

            {submitted ? (
              <div ref={panelRef} className="bg-card rounded-xl shadow-lg p-8 md:p-10 w-full text-center">
                <div className="mx-auto mb-4 h-14 w-14 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                  <span className="text-2xl">Checkmark</span>
                </div>
                <h2 className="text-2xl font-semibold mb-2">
                  {t('contactForm.toast.requestSubmitted', 'Request Submitted')}
                </h2>
                <p className="text-muted-foreground">
                  {t('contactForm.toast.requestSubmittedDesc', 'Our team will get back to you shortly.')}
                </p>
              </div>
            ) : duplicate ? (
              <div ref={panelRef} className="bg-card rounded-xl shadow-lg p-8 md:p-10 w-full text-center">
                <div className="mx-auto mb-4 h-14 w-14 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                  <span className="text-2xl">Warning</span>
                </div>
                <h2 className="text-2xl font-semibold mb-2">
                  {t('contactForm.toast.submissionError', 'Already Submitted')}
                </h2>
                <p className="text-muted-foreground">
                  {t('contactForm.toast.emailExists', 'You have already requested a free trial with this email.')}
                </p>
              </div>
            ) : (
              <div className="bg-card rounded-xl shadow-lg p-6 md:p-8 lg:p-10 w-full">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="firstName">{t('freeTrial.form.firstName')} *</Label>
                      <Input
                        id="firstName"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lastName">{t('freeTrial.form.lastName')} *</Label>
                      <Input
                        id="lastName"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="email">{t('freeTrial.form.email')} *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="company">{t('freeTrial.form.company')} *</Label>
                      <Input
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="phone">{t('freeTrial.form.phone')} *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="country">{t('freeTrial.form.country')} *</Label>
                      <Select
                        value={formData.country}
                        onValueChange={(value) => setFormData(prev => ({ ...prev, country: value }))}
                        required
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={t('freeTrial.form.selectCountry')} />
                        </SelectTrigger>
                        <SelectContent>
                          {countries.map((country) => (
                            <SelectItem key={country} value={country}>{country}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="cameraCount">{t('freeTrial.form.cameraCount')} *</Label>
                      <Input
                        id="cameraCount"
                        name="cameraCount"
                        type="number"
                        min="1"
                        max="100"
                        value={formData.cameraCount}
                        onChange={handleInputChange}
                        required
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        {t('freeTrial.form.cameraCountHelp')}
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="channelSegment">{t('freeTrial.form.channelSegment')} *</Label>
                      <Select
                        value={formData.channelSegment}
                        onValueChange={(value) => setFormData(prev => ({ ...prev, channelSegment: value }))}
                        required
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={t('freeTrial.form.selectSegment')} />
                        </SelectTrigger>
                        <SelectContent>
                          {channelSegments.map((segment) => (
                            <SelectItem key={segment.value} value={segment.value}>
                              {segment.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground mt-1">
                        {t('freeTrial.form.segmentHelp')}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-start space-x-2">
                      <Checkbox
                        id="agreeTerms"
                        checked={formData.agreeTerms}
                        onCheckedChange={(checked) => setFormData(prev => ({ ...prev, agreeTerms: checked as boolean }))}
                        required
                      />
                      <label htmlFor="agreeTerms" className="text-sm font-medium leading-none">
                        {t('freeTrial.form.agreeToTerms')} *
                      </label>
                    </div>

                    <div className="flex items-start space-x-2">
                      <Checkbox
                        id="subscribe"
                        checked={formData.subscribe}
                        onCheckedChange={(checked) => setFormData(prev => ({ ...prev, subscribe: checked as boolean }))}
                      />
                      <label htmlFor="subscribe" className="text-sm font-medium leading-none">
                        {t('freeTrial.form.subscribeToNewsletter')}
                      </label>
                    </div>
                  </div>

                  <div className="pt-2 flex justify-center">
                    <Button 
                      type="submit" 
                      size="lg" 
                      className="w-full md:w-auto px-8 py-6 text-base" 
                      disabled={submitting}
                    >
                      {submitting ? 'Submitting...' : t('freeTrial.form.startFreeTrial')}
                    </Button>
                  </div>

                  <p className="text-sm text-muted-foreground text-center">
                    {t('freeTrial.form.noCreditCard')}
                  </p>
                </form>
              </div>
            )}
          </div>
        </div>
      </main>

      <Partners />
      <Footer />
    </div>
  );
}